import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { useAnalytics as useAnalyticsBase, type TConvertEventsMap } from '@experience/frontend-analytics';
import { makeUseViewTracker, makeUseClickTracker } from '@experience/vue-analytics-directives';

import type { EventsMap } from '@/shared/api/generated/sogu/types.gen';
import { MF_NAME } from '@/shared/config/env';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { makeSingleton } from '@/shared/utils/makeSingleton';

const createAnalytics = () => {
  const { userId, deviceId } = getBootstrapData();
  const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();

  type AnalyticsEventsMap = TConvertEventsMap<EventsMap>;

  const { trackEvent, trackEventImmediately } = useAnalyticsBase<AnalyticsEventsMap>({
    soguURL: VITE_RUNTIME_SOGU_SERVICE_URL,
    appName: MF_NAME,
    appVersion: __APP_VERSION__,
    userId: userId ? String(userId) : null,
    deviceId,
  });

  /**
   * Позволяет отслеживать просмотры элементов на странице.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useViewTracker = makeUseViewTracker<AnalyticsEventsMap>(trackEvent);

  /**
   * Позволяет отслеживать просмотры элементов на странице.
   *
   * Отправляет событие даже после ухода со страницы.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useImmediateViewTracker = makeUseViewTracker<AnalyticsEventsMap>(trackEventImmediately);

  /**
   * Позволяет отслеживать клики на элементах на странице.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useClickTracker = makeUseClickTracker<AnalyticsEventsMap>(trackEvent);

  /**
   * Позволяет отслеживать клики на элементах на странице.
   *
   * Отправляет событие даже после ухода со страницы.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useImmediateClickTracker = makeUseClickTracker<AnalyticsEventsMap>(trackEventImmediately);

  return {
    trackEvent,
    trackEventImmediately,
    useViewTracker,
    useImmediateViewTracker,
    useClickTracker,
    useImmediateClickTracker,
  };
};

export type Analytics = ReturnType<typeof createAnalytics>;

const { getInstance, setInstance, resetInstance } = makeSingleton(createAnalytics);

export const useAnalytics = getInstance;
export const setAnalytics = setInstance;
export const resetAnalytics = resetInstance;
